import React, { useEffect, useMemo, useState } from "react";

import { useToast } from "../../../components";
import { useSetAiInterviewerUserRolesMutation } from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import RoleSelect from "../RoleSelect";

type SelectUserRole = {
  id: string;
  name: string;
  formattedPermissions: string;
  formattedName: string;
};

interface ScreenRoleSelectProps {
  userId: string;
  defaultValue: string;
  showDescription?: boolean;
}

const ScreenRoleSelect: React.FC<ScreenRoleSelectProps> = ({
  userId,
  defaultValue,
  showDescription = false,
}) => {
  const currentUser = useCurrentUser();
  const toast = useToast();
  const [aiInterviewerUserRole, setAiInterviewerUserRole] =
    useState<SelectUserRole>();

  const [setUserRoles] = useSetAiInterviewerUserRolesMutation({
    onCompleted: (data) => {
      if (data.setAiInterviewerUserRoles) {
        toast({
          title: "Success",
          description: "Updated Screen user role",
          status: "success",
        });
      }
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    },
  });

  const { aiInterviewerUserRoles } = currentUser.organization;
  const roleOptions: SelectUserRole[] = useMemo(
    () => [
      {
        id: "",
        name: "NO_ACCESS",
        formattedPermissions: "No access to BrightHire Screen",
        formattedName: "No Access",
      },
      ...(aiInterviewerUserRoles || []).map((role) => ({
        id: role.id,
        name: role.name,
        formattedPermissions: role.formattedPermissions || "",
        formattedName: role.formattedName || role.name,
      })),
    ],
    [aiInterviewerUserRoles]
  );

  const canModify =
    userId !== currentUser.id && currentUser.userRole?.canManageAiInterviewers;

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selectedRole = roleOptions.find((r) => r.id === e.target.value);
    if (!selectedRole) return;

    setAiInterviewerUserRole(selectedRole);
    setUserRoles({
      variables: {
        users: [
          {
            userId,
            aiInterviewerUserRoleId:
              e.target.value === "" ? null : e.target.value,
          },
        ],
      },
    });
  };

  useEffect(() => {
    if (!aiInterviewerUserRole && defaultValue) {
      const defaultRole = roleOptions.find((r) => r.id === defaultValue);
      if (defaultRole) {
        setAiInterviewerUserRole(defaultRole);
      }
    }
  }, [defaultValue, roleOptions, aiInterviewerUserRole]);

  return (
    <RoleSelect
      userRole={aiInterviewerUserRole}
      formId={`screen-role-select-${userId}`}
      showDescription={showDescription}
      onChange={onChange}
      defaultValue={defaultValue}
      userRoles={roleOptions}
      canModify={canModify}
      selectProps={{ "data-testid": "screen-role-select" }}
    />
  );
};

export default ScreenRoleSelect;
